// src/components/MasterComingSoon.jsx
//
// Per request, the "Master" sidebar link now opens straight to this page
// instead of toggling an (empty, since Role/Department/Company were
// removed from under it) submenu -- see SideMenu.jsx, where the Master
// button was changed from a collapse-toggle to a plain NavLink to
// "/master". Content/design below is a faithful port of the uploaded
// Release.html "Coming Soon" card: same markup/CSS, just scoped under
// ".master-coming-soon" (the original used unscoped `body`/`h1`/etc.
// selectors, which would have leaked into the rest of the app once
// rendered here) and with the embedded base64 background image pulled
// out to its own file (public/assets/images/master-coming-soon-bg.png)
// instead of a ~2MB base64 string sitting inline in this component. The
// outer full-viewport glow circles / dark page background from the
// original are dropped -- this stays inside the app's normal layout
// (header + sidebar visible, same as every other page) rather than
// covering the whole browser window. Within that content area, though,
// it fills the ENTIRE available space edge-to-edge: negative margins on
// ".master-coming-soon" cancel out .content-page's + .container-fluid's
// combined horizontal padding (1.25rem combined, from app.css), and the
// card's min-height uses the same "100vh - topbar height" formula
// .content-page itself uses, so there's no white margin/gutter showing
// around it like the original padded-card version had. Square corners
// (border-radius removed on the card/frame) per an earlier request.
import React from "react";

export default function MasterComingSoon() {
  return (
    <div className="master-coming-soon">
      <style>{`
        .master-coming-soon{
          --mcs-deep:#133c40;
          --mcs-deep-2:#123a3e;
          --mcs-dark:#14403f;
          --mcs-green:#36916e;
          --mcs-mint:#40ae80;
          --mcs-white:#ffffff;
          display:block;
          margin-inline:-1.25rem;
        }

        .master-coming-soon .release-card{
          position:relative;
          width:100%;
          min-height:calc(100vh - (var(--theme-topbar-height, 65px) + 1px));
          overflow:hidden;
          border:0;
          border-radius:0;
          background:var(--mcs-deep);
          isolation:isolate;
        }

        .master-coming-soon .airport-scene{
          position:absolute;
          inset:0;
        }

        .master-coming-soon .airport-scene img{
          width:100%;
          height:100%;
          display:block;
          object-fit:cover;
          object-position:center center;
          filter:saturate(.91) contrast(1.04) brightness(.88);
          animation:mcsCinematicDrift 16s ease-in-out infinite alternate;
        }

        .master-coming-soon .airport-scene::after{
          content:"";
          position:absolute;
          inset:0;
          background:
            linear-gradient(90deg,rgba(19,60,64,.99) 0%,rgba(19,60,64,.96) 30%,rgba(19,60,64,.70) 48%,rgba(19,60,64,.12) 76%,rgba(19,60,64,.06) 100%),
            linear-gradient(0deg,rgba(9,37,38,.66),transparent 42%),
            linear-gradient(135deg,rgba(64,174,128,.13),transparent 44%);
        }

        .master-coming-soon .frame-line{
          position:absolute;
          z-index:3;
          inset:28px;
          border:1px solid rgba(255,255,255,.12);
          border-radius:0;
          pointer-events:none;
        }

        .master-coming-soon .content{
          position:relative;
          z-index:6;
          width:48%;
          min-height:calc(100vh - (var(--theme-topbar-height, 65px) + 1px));
          display:flex;
          flex-direction:column;
          justify-content:center;
          padding:70px 38px 70px 76px;
        }

        .master-coming-soon .release-label{
          width:max-content;
          display:inline-flex;
          align-items:center;
          gap:11px;
          padding:10px 15px;
          border:1px solid rgba(255,255,255,.15);
          border-radius:999px;
          background:rgba(255,255,255,.065);
          color:rgba(255,255,255,.86);
          font-size:.75rem;
          font-weight:800;
          letter-spacing:.16em;
          text-transform:uppercase;
          backdrop-filter:blur(8px);
        }

        .master-coming-soon .release-label::before{
          content:"";
          width:8px;
          height:8px;
          border-radius:50%;
          background:var(--mcs-mint);
          box-shadow:0 0 0 0 rgba(64,174,128,.52);
          animation:mcsPulse 2s infinite;
        }

        .master-coming-soon h1{
          max-width:520px;
          margin:30px 0 24px;
          color:var(--mcs-white);
          font-size:clamp(4rem,7.3vw,7.3rem);
          line-height:.88;
          font-weight:800;
          letter-spacing:-.07em;
        }

        .master-coming-soon h1::after{
          content:"";
          display:block;
          width:82px;
          height:5px;
          margin-top:30px;
          border-radius:99px;
          background:linear-gradient(90deg,var(--mcs-green),var(--mcs-mint));
        }

        .master-coming-soon .message{
          margin:0;
          color:rgba(255,255,255,.68);
          font-size:1.06rem;
          line-height:1.72;
        }

        .master-coming-soon .takeoff-graphic{
          position:absolute;
          z-index:4;
          top:50%;
          right:4.8%;
          width:min(470px,41vw);
          aspect-ratio:1;
          transform:translateY(-50%);
          border:1px solid rgba(255,255,255,.12);
          border-radius:50%;
          box-shadow:0 0 0 52px rgba(255,255,255,.025),0 0 0 104px rgba(255,255,255,.012);
          pointer-events:none;
        }

        .master-coming-soon .takeoff-graphic::before{
          content:"";
          position:absolute;
          inset:24px;
          border:1px dashed rgba(255,255,255,.22);
          border-radius:50%;
          animation:mcsRotate 34s linear infinite;
        }

        .master-coming-soon .takeoff-graphic::after{
          content:"";
          position:absolute;
          left:50%;
          bottom:14%;
          width:3px;
          height:39%;
          border-radius:99px;
          transform:translateX(-50%);
          background:repeating-linear-gradient(180deg,rgba(255,255,255,.76) 0 14px,transparent 14px 27px);
          opacity:.72;
        }

        .master-coming-soon .runway-guide{
          position:absolute;
          z-index:4;
          right:8.5%;
          bottom:8%;
          width:34%;
          height:32%;
          opacity:.34;
          clip-path:polygon(46% 0,54% 0,100% 100%,0 100%);
          border-left:3px solid var(--mcs-mint);
          border-right:3px solid var(--mcs-mint);
          background:linear-gradient(180deg,transparent,rgba(19,60,64,.24));
          pointer-events:none;
        }

        .master-coming-soon .status{
          position:absolute;
          z-index:8;
          right:50px;
          bottom:52px;
          display:flex;
          align-items:center;
          gap:13px;
          padding:14px 18px;
          border:1px solid rgba(255,255,255,.16);
          border-radius:16px;
          background:rgba(19,60,64,.82);
          color:var(--mcs-white);
          box-shadow:0 18px 42px rgba(0,20,20,.30);
          backdrop-filter:blur(12px);
          font-size:.78rem;
          font-weight:800;
          letter-spacing:.08em;
          text-transform:uppercase;
        }

        .master-coming-soon .status-icon{
          width:36px;
          height:36px;
          display:grid;
          place-items:center;
          flex:0 0 auto;
          border-radius:50%;
          color:var(--mcs-white);
          background:linear-gradient(145deg,var(--mcs-green),var(--mcs-mint));
          box-shadow:0 0 0 7px rgba(64,174,128,.11);
        }

        .master-coming-soon .status-icon svg{width:19px;height:19px;transform:rotate(-35deg)}

        @keyframes mcsPulse{
          70%{box-shadow:0 0 0 9px rgba(64,174,128,0)}
          100%{box-shadow:0 0 0 0 rgba(64,174,128,0)}
        }

        @keyframes mcsRotate{to{transform:rotate(360deg)}}

        @keyframes mcsCinematicDrift{
          from{transform:scale(1.01)}
          to{transform:scale(1.055)}
        }

        @media (max-width:991.98px){
          .master-coming-soon .airport-scene img{object-position:64% center}
          .master-coming-soon .airport-scene::after{
            background:
              linear-gradient(180deg,rgba(19,60,64,.97) 0%,rgba(19,60,64,.89) 38%,rgba(19,60,64,.30) 63%,rgba(9,37,38,.68) 100%),
              linear-gradient(90deg,rgba(19,60,64,.36),transparent 70%);
          }
          .master-coming-soon .content{width:100%;min-height:auto;align-items:center;padding:70px 38px 30px;text-align:center}
          .master-coming-soon h1{max-width:760px}
          .master-coming-soon h1::after{margin-left:auto;margin-right:auto}
          .master-coming-soon .takeoff-graphic{top:auto;right:50%;bottom:8%;width:min(470px,72vw);transform:translateX(50%)}
          .master-coming-soon .runway-guide{right:28%;bottom:3%;width:44%}
          .master-coming-soon .status{right:50%;bottom:44px;transform:translateX(50%)}
        }

        @media (max-width:575.98px){
          .master-coming-soon .frame-line{inset:15px}
          .master-coming-soon .content{padding:54px 24px 24px}
          .master-coming-soon h1{margin-top:25px;font-size:clamp(3.55rem,18vw,5.2rem)}
          .master-coming-soon h1::after{margin-top:24px}
          .master-coming-soon .message{font-size:.96rem}
          .master-coming-soon .takeoff-graphic{bottom:10%;width:78vw}
          .master-coming-soon .status{bottom:34px;white-space:nowrap;padding:12px 15px}
        }

        @media (prefers-reduced-motion:reduce){
          .master-coming-soon *,
          .master-coming-soon *::before,
          .master-coming-soon *::after{animation-duration:.01ms!important;animation-iteration-count:1!important}
        }
      `}</style>

      <section className="release-card" aria-labelledby="master-release-title">
        <div className="airport-scene" aria-hidden="true">
          <img src="/assets/images/master-coming-soon-bg.png" alt="" />
        </div>

        <div className="frame-line" aria-hidden="true" />
        <div className="takeoff-graphic" aria-hidden="true" />
        <div className="runway-guide" aria-hidden="true" />

        <div className="content">
          <div className="release-label">Next Release</div>
          <h1 id="master-release-title">Coming Soon.</h1>
          <p className="message">
            We&rsquo;re preparing the next experience.
            <br />
            Please bear with us.
          </p>
        </div>

        <div className="status">
          <span className="status-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M21.8 16.5 14 12V6.5c0-1.1-.9-2.5-2-2.5s-2 1.4-2 2.5V12l-7.8 4.5c-.4.2-.7.7-.7 1.2v1.1L10 16.6V21l-2 1.3V23l4-1 4 1v-.7L14 21v-4.4l8.5 2.2v-1.1c0-.5-.3-1-.7-1.2Z"
              />
            </svg>
          </span>
          <span>Ready for take-off</span>
        </div>
      </section>
    </div>
  );
}
